import React from "react";
import SubpageHeader from "../components/SubpageHeader";
import SiteFooter from "../components/SiteFooter";
import TrustPageIntro from "../components/TrustPageIntro";
import Button from "../components/ui/Button";
import Typography from "../components/ui/Typography";
import usePageMeta from "../hooks/usePageMeta";
import { openConsentPreferences } from "../utils/analytics";
import { handleInternalLinkClick } from "../utils/navigation";
import { buildStaticPageSeoMeta } from "./static/staticPageSeo";

const privacyFacts = [
  {
    title: "No account needed",
    copy: "PremiereLens never asks for your name, email address or login.",
  },
  {
    title: "My List stays local",
    copy: "Saved shows live in this browser's local storage, not on PremiereLens servers.",
  },
  {
    title: "Analytics are optional",
    copy: "Google Analytics does not load, and nothing is sent to Google, until you allow it. You can change your choice at any time.",
  },
];

const policySections = [
  {
    title: "Information we collect",
    body: [
      "We do not collect personal information that identifies you, such as your name or email address.",
      "If you allow analytics, we use Google Analytics 4 to see which pages and features are useful, in aggregate. We do not use analytics consent to create accounts, sell personal information or personalise ads.",
    ],
  },
  {
    title: "Local storage and My List",
    body: [
      "My List keeps its data in your browser's local storage, so saved shows stay on your device and out of any PremiereLens account system.",
      "Clearing browser data may remove your list. You control exporting and importing it.",
    ],
  },
  {
    title: "Archive search",
    body: [
      "Searching the archive by title, tag, source or month works in your browser.",
      "If you choose Search by meaning, the words you type are sent to PremiereLens servers and to OpenAI, so they can be matched against the archive. My List, your identity, browsing history and analytics choice are not sent with them.",
    ],
  },
  {
    title: "Cookies and tracking",
    body: [
      "Cookies are small text files stored on your device. Google Analytics is not loaded, and sets no cookies, until you allow analytics.",
      "Google Consent Mode v2 keeps ad storage denied either way. If you withdraw your choice, analytics stops sending data, though cookies it already set stay until you clear them in your browser. Your analytics choice is stored in this browser's local storage.",
    ],
  },
  {
    title: "Advertising",
    body: [
      "We do not currently display ads. Marketing consent is kept separate from analytics and stays denied unless we add and explain advertising features.",
    ],
  },
  {
    title: "Do Not Track",
    body: [
      "We do not respond to Do Not Track signals, which browsers and websites do not apply consistently.",
    ],
  },
  {
    title: "Children's privacy",
    body: [
      "This site is not intended for children under 13, and we do not knowingly collect personal information from children.",
    ],
  },
  {
    title: "Data security",
    body: [
      "We take reasonable steps to secure the site and protect information from unauthorised access. However, no method of transmission over the internet is completely secure.",
    ],
  },
  {
    title: "Changes to this policy",
    body: [
      "We may update this policy from time to time. The effective date will be updated at the top of this page.",
    ],
  },
  {
    title: "Links to other websites",
    body: [
      "Our site may link to external websites. We are not responsible for their privacy practices or content.",
    ],
  },
];

const PrivacyPage = () => {
  usePageMeta(buildStaticPageSeoMeta("privacy"));

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SubpageHeader />

      <main id="main-content" tabIndex={-1} className="route-shell route-shell--editorial">
        <div className="mx-auto max-w-5xl space-y-10">
          <TrustPageIntro
            title="What PremiereLens stores, and what it does not"
            description={(
              <>
                You can browse without an account, keep My List on your own device, and choose whether to allow
                analytics. Search by meaning sends only the words you type.
              </>
            )}
            actions={(
              <>
                <Button type="button" variant="outline" onClick={openConsentPreferences}>
                  Change analytics preferences
                </Button>
                <a
                  href="/faq#privacy"
                  onClick={(event) => handleInternalLinkClick(event, "/faq#privacy")}
                  className="inline-flex min-h-11 items-center rounded-md px-2 text-body-small font-semibold text-foreground/85 underline-offset-4 transition-colors hover:text-primary hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                >
                  Read privacy FAQs
                </a>
              </>
            )}
            footer={(
              <Typography variant="body-small" className="text-muted-foreground">
                Effective date: 2 November 2026
              </Typography>
            )}
          />

          <section className="grid gap-5 md:grid-cols-3">
            {privacyFacts.map((fact) => (
              <article key={fact.title} className="rounded-lg border border-accent-gold/45 bg-card/45 p-5">
                <Typography as="h2" variant="h3">
                  {fact.title}
                </Typography>
                <Typography variant="body" className="mt-3 text-foreground/85">
                  {fact.copy}
                </Typography>
              </article>
            ))}
          </section>

          <section className="grid gap-5 md:grid-cols-[minmax(0,0.85fr)_minmax(0,1.6fr)] md:gap-8">
            <div>
              <Typography as="h2" variant="h2">
                Full policy
              </Typography>
              <Typography variant="body" className="mt-3 text-muted-foreground">
                The whole policy is written out here, with nothing to expand.
              </Typography>
            </div>

            <div className="grid gap-6">
              {policySections.map((section) => (
                <article
                  key={section.title}
                  className="border-b border-dashed border-accent-gold/45 pb-6 last:border-b-0 last:pb-0"
                >
                  <Typography as="h3" variant="h3">
                    {section.title}
                  </Typography>
                  <div className="mt-3 grid gap-3">
                    {section.body.map((paragraph) => (
                      <Typography key={paragraph} variant="body" className="max-w-3xl text-foreground/85">
                        {paragraph}
                      </Typography>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </section>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default PrivacyPage;
